import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

const MOBILE_BREAKPOINT = 768;

function getIsMobileViewport() {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < MOBILE_BREAKPOINT;
}

const SidebarContext = createContext({
  collapsed: false,
  toggle: () => {},
  isMobile: false,
  mobileOpen: false,
  toggleSidebar: () => {},
  openMobileSidebar: () => {},
  closeMobileSidebar: () => {},
});

export function SidebarProvider({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(getIsMobileViewport);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleResize = () => {
      const nextIsMobile = getIsMobileViewport();
      setIsMobile(nextIsMobile);
      if (!nextIsMobile) {
        setMobileOpen(false);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggle = useCallback(() => setCollapsed((value) => !value), []);
  const openMobileSidebar = useCallback(() => setMobileOpen(true), []);
  const closeMobileSidebar = useCallback(() => setMobileOpen(false), []);
  const toggleSidebar = useCallback(() => {
    if (isMobile) {
      setMobileOpen((value) => !value);
      return;
    }
    setCollapsed((value) => !value);
  }, [isMobile]);

  const value = useMemo(() => ({
    collapsed,
    toggle,
    isMobile,
    mobileOpen,
    toggleSidebar,
    openMobileSidebar,
    closeMobileSidebar,
  }), [
    closeMobileSidebar,
    collapsed,
    isMobile,
    mobileOpen,
    openMobileSidebar,
    toggle,
    toggleSidebar,
  ]);

  return (
    <SidebarContext.Provider value={value}>
      {children}
    </SidebarContext.Provider>
  );
}

export function useSidebar() {
  return useContext(SidebarContext);
}
